/**
 * One announcement, as a card inside its BU's section.
 *
 * The action row encodes the approval workflow: a draft is sent, a pending item belonging to
 * someone else is approved or rejected, and the creator of a pending item sees an explicit
 * "Outro aprovador" marker instead of disabled buttons — so it reads as a rule, not a bug.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import { GLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';
import type { WhatsNew } from '../../Types/WhatsNew.types';
import { getRejectionLine, getStatusPresentation } from '../../Utils/approvalRules';
import type { WhatsNewActions } from '../../Utils/approvalRules';
import { preloadWhatsNewPreview } from '@/Shared/brand';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

/*
  The version gets the weight of a chip, not of a caption.

  It is the one field on this card that decides whether the announcement reaches anyone: an item
  pinned to a version the BU has already moved past is delivered to nobody, and the mistake is
  invisible afterwards. It used to read as muted metadata beside the status.
*/
const versionStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-round);
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  white-space: nowrap;
`;

/** The BU's current version, so the chip reads as "current" rather than just as a number. */
const versionCurrentStyle = css`
  border-color: var(--color-success);
  background: var(--color-success-subtle);
  color: var(--gl-green-dark);
`;

const versionStaleStyle = css`
  border-color: var(--color-warning);
  background: var(--color-warning-subtle);
  color: var(--gl-yellow-dark);
`;

const spacerStyle = css`
  flex: 1;
`;

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const titleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  line-height: 1.3;
  text-wrap: pretty;
`;

const messageStyle = css`
  font-size: var(--font-size-xs);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const rejectionStyle = css`
  display: block;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  background: var(--color-danger-subtle);
  color: var(--color-text);
  font-size: var(--font-size-2xs);
  line-height: 1.45;
  text-wrap: pretty;
`;

const metaStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
`;

/*
  Where the announcement shows up, as a chip rather than a line of grey mono.

  The three cases behave differently and used to look identical: a specific page, the BU's main
  page (which is what "no page chosen" turns into, since the BFF assigns it on create), and no
  page at all — which only happens when the BU had no main page, and which the dashboard reaches
  only on a fetch that sends no page slug.
*/
const pageChipStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  max-width: 100%;
  padding: 2px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-round);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-2xs);
  color: var(--color-text);
`;

const pageMainStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
`;

const pageNoneStyle = css`
  border-color: var(--color-warning);
  background: var(--color-warning-subtle);
  color: var(--gl-yellow-dark);
`;

const pageSlugStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const pageTagStyle = css`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
`;

const authorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/*
  A tooltip is not enough for a failure this quiet: the announcement stays approved and active,
  and simply never reaches anyone. So the mismatch is also stated in the card.
*/
const staleNoteStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: var(--font-size-2xs);
  line-height: 1.4;
  color: var(--gl-yellow-dark);
  text-wrap: pretty;
`;

/* Only clickable for whoever can act on the list; for everyone else it stays a plain caption. */
const reachButtonStyle = css`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  color: var(--color-primary);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const reachStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

/*
  Ver on one side, the workflow buttons grouped on the other — not a row of buttons around a flex
  spacer. With a spacer the row wrapped one button at a time: at three cards per row "Ver" ended
  up alone on the first line with the spacer and Rejeitar/Aprovar broken across the next. This way
  the right-hand group drops to its own line whole, which still reads as a footer.
*/
const actionsStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-s);
  flex-wrap: wrap;
  margin-top: auto;
`;

const actionGroupStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  /* The pair is one unit: Rejeitar must never sit on a line of its own, away from Aprovar. */
  flex-wrap: nowrap;
`;

const blockedStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border-radius: var(--radius-control);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
`;

/** Short, locale-aware date for the card footer. */
function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export interface WhatsNewCardProps {
  item: WhatsNew;
  /**
   * The BU's current platform version, to flag an announcement pinned to an older one.
   *
   * This matters more than it looks: the dashboard asks for its own compile-time version, so an
   * announcement left on a version the BU has moved past stops being served — permanently and
   * with no trace. It stays APPROVED, active and unseen.
   */
  tenantVersion?: string;
  /** Whether the announcement's page is the BU's main one — where a pageless choice lands. */
  isMainPage?: boolean;
  actions: WhatsNewActions;
  /** True right after the JSON was copied, to flip the icon to a check. */
  isCopied: boolean;
  onCopy: (item: WhatsNew) => void;
  onView: (item: WhatsNew) => void;
  onEdit: (item: WhatsNew) => void;
  onSubmit: (item: WhatsNew) => void;
  onApprove: (item: WhatsNew) => void;
  onReject: (item: WhatsNew) => void;
  onDelete: (item: WhatsNew) => void;
  /** Absent hides the viewer list entirely — it is gated on a permission, not just disabled. */
  onViewers?: (item: WhatsNew) => void;
}

export const WhatsNewCard = ({
  item,
  tenantVersion,
  isMainPage = false,
  actions,
  isCopied,
  onCopy,
  onView,
  onEdit,
  onSubmit,
  onApprove,
  onReject,
  onDelete,
  onViewers,
}: WhatsNewCardProps): JSX.Element => {
  const presentation = getStatusPresentation(item.approvalStatus);
  const itemVersion = item.platformVersion?.version;
  const isCurrentVersion = tenantVersion !== undefined && itemVersion === tenantVersion;
  const isStaleVersion =
    tenantVersion !== undefined && itemVersion !== undefined && itemVersion !== tenantVersion;
  const rejectionLine = getRejectionLine(item);
  const date = formatDate(item.updatedAt || item.createdAt);

  return (
    <article
      id={`whats-new-card-${item.id}`}
      className={cardStyle}
      /*
        The preview renders in the tenant's own brand build, which is a separate bundle. Warming it
        while the pointer is on the card means the modal is there the moment Ver is clicked.
      */
      onMouseEnter={() => {
        preloadWhatsNewPreview(item.tenantSlug);
      }}
      onFocus={() => {
        preloadWhatsNewPreview(item.tenantSlug);
      }}
    >
      <div className={headerStyle}>
        <StatusTag status={presentation.status} size='sm'>
          {presentation.label}
        </StatusTag>
        <span
          className={`${versionStyle} ${isCurrentVersion ? versionCurrentStyle : ''} ${
            isStaleVersion ? versionStaleStyle : ''
          }`}
          title={
            isStaleVersion
              ? `A versão atual de ${item.tenantSlug} é v${tenantVersion}. Um anúncio preso a outra versão deixa de ser entregue.`
              : undefined
          }
        >
          <GLIcon
            variant={isStaleVersion ? 'Notifications.Warning' : 'Misc.Tag'}
            size={12}
            fill='currentColor'
          />
          v{item.platformVersion?.version}
        </span>
        <div className={spacerStyle} />
        <IconButton
          id={`whats-new-copy-${item.id}`}
          icon={isCopied ? 'Communication.Check' : 'Communication.CopySimple'}
          size='sm'
          aria-label={isCopied ? 'JSON copiado' : 'Copiar JSON do anúncio'}
          onClick={() => {
            onCopy(item);
          }}
        />
        {actions.canEdit ? (
          <IconButton
            id={`whats-new-edit-${item.id}`}
            icon='Settings.PencilSimple'
            size='sm'
            aria-label={`Editar anúncio ${item.welcomeMessage}`}
            onClick={() => {
              onEdit(item);
            }}
          />
        ) : null}
        {actions.canDelete ? (
          <IconButton
            id={`whats-new-delete-${item.id}`}
            icon='Misc.Trash'
            size='sm'
            aria-label={`Excluir anúncio ${item.welcomeMessage}`}
            onClick={() => {
              onDelete(item);
            }}
          />
        ) : null}
      </div>

      <div className={contentStyle}>
        <span className={titleStyle}>{item.welcomeMessage}</span>
        {item.subtitle ? <span className={messageStyle}>{item.subtitle}</span> : null}
      </div>

      {rejectionLine ? <span className={rejectionStyle}>{rejectionLine}</span> : null}

      <div className={metaStyle}>
        {item.page ? (
          <span
            className={`${pageChipStyle} ${isMainPage ? pageMainStyle : ''}`}
            /* The slug lives in the tooltip: it is what the dashboard routes on, so it still has
               to be reachable, just not at the cost of reading the name. */
            title={
              isMainPage
                ? `${item.page.slug} · página principal da ${item.tenantSlug}, que é onde cai um anúncio sem página escolhida.`
                : `${item.page.slug} · aparece só nesta página da ${item.tenantSlug}.`
            }
          >
            <GLIcon
              variant={isMainPage ? 'Menu.SquaresFour' : 'Misc.Eye'}
              size={12}
              fill='currentColor'
            />
            <span className={pageSlugStyle}>{item.page.name || item.page.slug}</span>
            {isMainPage ? <span className={pageTagStyle}>principal</span> : null}
          </span>
        ) : (
          <span
            className={`${pageChipStyle} ${pageNoneStyle}`}
            title={`A ${item.tenantSlug} não tinha página principal quando este anúncio foi criado.`}
          >
            <GLIcon variant='Notifications.Warning' size={12} fill='currentColor' />
            <span className={pageSlugStyle}>sem página</span>
          </span>
        )}
        <span className={authorStyle}>
          {item.createdBy?.name ?? 'autor desconhecido'}
          {date ? ` · ${date}` : ''}
        </span>
        {isStaleVersion ? (
          <span className={staleNoteStyle}>
            <GLIcon variant='Notifications.Warning' size={13} fill='var(--gl-yellow-dark)' />
            Preso à v{itemVersion}, mas {item.tenantSlug} já está na v{tenantVersion} — não é
            entregue enquanto estiver assim.
          </span>
        ) : null}

        {actions.hasReach && item.viewerCount !== undefined ? (
          onViewers ? (
            <button
              type='button'
              id={`whats-new-viewers-${item.id}`}
              className={`${reachStyle} ${reachButtonStyle}`}
              onClick={() => {
                onViewers(item);
              }}
            >
              <GLIcon variant='Misc.Eye' size={14} fill='currentColor' />
              {item.viewerCount.toLocaleString('pt-BR')}{' '}
              {item.viewerCount === 1 ? 'usuário viu' : 'usuários viram'}
            </button>
          ) : (
            <span className={reachStyle}>
              <GLIcon variant='Misc.Eye' size={14} fill='var(--color-text-muted)' />
              {item.viewerCount.toLocaleString('pt-BR')}{' '}
              {item.viewerCount === 1 ? 'usuário viu' : 'usuários viram'}
            </span>
          )
        ) : null}
      </div>

      <div className={actionsStyle}>
        <UTButton
          fitContent
          id={`whats-new-view-${item.id}`}
          variant='tertiary'
          size='small'
          text='Ver'
          icon='Misc.Eye'
          onClick={() => {
            onView(item);
          }}
        />

        <div className={actionGroupStyle}>
          {actions.canApprove ? (
            <>
              <UTButton
                fitContent
                id={`whats-new-reject-${item.id}`}
                variant='secondary'
                size='small'
                text='Rejeitar'
                onClick={() => {
                  onReject(item);
                }}
              />
              <UTButton
                fitContent
                id={`whats-new-approve-${item.id}`}
                size='small'
                text='Aprovar'
                icon='Communication.Check'
                onClick={() => {
                  onApprove(item);
                }}
              />
            </>
          ) : null}

          {actions.isBlockedFromApproving ? (
            <span className={blockedStyle}>
              <GLIcon variant='Misc.Lock' size={14} fill='var(--color-text-muted)' />
              Outro aprovador
            </span>
          ) : null}

          {actions.canSubmit ? (
            <UTButton
              fitContent
              id={`whats-new-submit-${item.id}`}
              size='small'
              text='Enviar'
              icon='Arrows.ArrowRight'
              onClick={() => {
                onSubmit(item);
              }}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
};
